/*
 * File Path: client/src/api/apiClient.js
 * Description: Configures the Axios instance for API calls, including base URL, interceptors, and error handling.
 * Developed by: M. YOUCEF Yazid (yazid.youcef@gmail.com)
 * BDAO-SIS Project
 * Version: 1.0.0
 * CreateDate: 2025-05-01
 * UpdateDate: 2025-05-01
 */

import axios from 'axios';
import i18n from '../core/i18n'; // Assuming i18n setup is in core

// Create the Axios instance
const apiClient = axios.create({
    baseURL: `/api`, // Using relative path (Requires proxy in dev server)
    withCredentials: true, // Crucial for session cookies
});

// Centralized error handler using i18n.t()
const handleApiError = (error) => {
    // Initial check for /auth/me 401 to suppress console noise during startup
    if (error.response?.status === 401 && error.config?.url === '/auth/me') {
        console.log("[handleApiError] Suppressing logs for initial 401 on /auth/me.");
        throw error; // Re-throw for AuthContext to handle silently
    }

    if (error.response) {
        // Server responded with a non-2xx status code
        console.error("[handleApiError] API Error Response:");
        console.error("  Status:", error.response.status);
        console.error("  Data:", error.response.data);
        // console.error("  Headers:", error.response.headers); // Usually too verbose

        const messageFromServer = error.response.data?.message;
        const userIdFromServer = error.response.data?.userId; // For activation flow
        let translationKey = 'errors.api.requestFailed'; // Default fallback
        let errorToThrow = new Error();

        switch (error.response.status) {
            case 400: // Bad Request
                // Example: Specific message code handling
                if (messageFromServer === 'USERNAME_PASSWORD_REQUIRED') {
                    translationKey = 'login.errors.usernamePasswordRequired';
                }
                // Add more specific 400 errors if needed
                break;
            case 401: // Unauthorized
                // The interceptor handles redirection for non-/me 401s.
                // This handles specific 401 *messages* if needed, e.g., invalid credentials.
                if (messageFromServer === 'INVALID_CREDENTIALS') {
                    translationKey = 'login.errors.invalidCredentials';
                }
                // Note: '/auth/me' 401s are handled at the top.
                break;
            case 403: // Forbidden
                if (messageFromServer === 'ACCOUNT_NOT_ACTIVE') {
                    translationKey = 'login.errors.accountNotActive';
                    errorToThrow = new Error(i18n.t(translationKey));
                    errorToThrow.userId = userIdFromServer; // Attach userId
                    errorToThrow.code = 'ACCOUNT_NOT_ACTIVE';
                    throw errorToThrow; // Throw specific error immediately
                }
                // Add other 403 handling if needed
                break;
            case 404: // Not Found
                // Often handled contextually (e.g., Profile not found)
                 translationKey = 'NotFound.errors.notFound'; // Or a more generic API 404
                break;
             case 409: // Conflict
                // Example: User exists during registration
                if (messageFromServer === 'Email already registered' || messageFromServer === 'Phone number already registered') {
                    translationKey = 'Register.errors.userExists';
                } else if (messageFromServer === 'A property with this name already exists for your account.') {
                    // Example for property name conflict
                    translationKey = 'properties.error.nameExists';
                }
                // Add other conflict codes
                break;
             case 410: // Gone (e.g., expired token)
                 if (messageFromServer === 'Activation code has expired.') {
                     translationKey = 'activation.errors.activationCodeExpired';
                 } else if (messageFromServer === 'Verification link has expired.') {
                      translationKey = 'verification.error.linkExpired'; // Example generic key
                 }
                 break;
            // Add cases for other statuses (422, 500, etc.) if specific handling is needed
            default:
                // Keep default key
                break;
        }

        // Try to use the messageFromServer as a translation key if it exists
        // and isn't handled by specific cases above.
        if (messageFromServer && i18n.exists(messageFromServer)) {
            errorToThrow = new Error(i18n.t(messageFromServer));
        } else if (messageFromServer && !i18n.exists(messageFromServer)){
             // If message exists but no translation, use the raw server message
             errorToThrow = new Error(messageFromServer);
             console.warn(`[handleApiError] Missing translation for server message key: "${messageFromServer}"`);
        } else {
            // Use the determined or default translation key
            errorToThrow = new Error(i18n.t(translationKey));
        }
        throw errorToThrow; // Throw the prepared/translated error

    } else if (error.request) {
        // No response received
        console.error("[handleApiError] API No Response Error:", error.request);
        throw new Error(i18n.t('errors.api.noResponse'));
    } else {
        // Error setting up the request
        console.error('[handleApiError] API Request Setup Error:', error.message);
        throw new Error(i18n.t('errors.api.requestSetupFailed'));
    }
};

// --- Axios Response Interceptor ---
apiClient.interceptors.response.use(
    (response) => response, // Pass through success
    (error) => {
        // Do not log the full error object here if handleApiError will also log it
        // console.error("Axios Interceptor Error:", error.message); // Log only the message

        // Handle 401 Unauthorized specifically for redirection (if not '/auth/me')
        if (error.response && error.response.status === 401) {
            if (error.config.url !== '/auth/me') {
                console.log("[Interceptor] 401 Unauthorized (not /auth/me). Redirecting to login...");
                // Redirect to login page
                if (window.location.pathname !== '/login') {
                     window.location.href = '/login'; // Force reload to clear state
                }
                // Reject promise to stop further processing in the original call chain
                // Use a more specific error message for the interceptor's action
                return Promise.reject(new Error("Session expired or invalid. Redirecting to login."));
            } else {
                 // Let 401 from '/auth/me' pass through to be handled by AuthContext/handleApiError
                 console.log("[Interceptor] 401 from /auth/me. Allowing specific handler.");
            }
        }
        // For non-401 errors OR the 401 from /auth/me, re-throw the error
        // so it can be handled by handleApiError or component catch blocks.
         return Promise.reject(error);
    }
);
// --- End Interceptor ---

export { apiClient, handleApiError };